import cn from 'classnames'
import {ErrorMessage, useFormikContext} from 'formik'
import {FC} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {AccountInfoCard} from '@/components/common/form/contact/AccountInfoCard'
import {useContactAccountState} from '@/components/common/form/contact/useContactAccountState'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {FormItem} from '@/components/common/form/FormItem'
import {FormItemsWrapper} from '@/components/common/form/FormItemsWrapper'
import {FormSection} from '@/components/common/form/FormSection'
import {FormSignUp} from '@/components/common/form/user-auth/FormSignUp'
import {TransLink} from '@/components/common/TransLink'
import {Checkbox} from '@/components/inputs/Checkbox'
import {MarketingConsent} from '@/components/inputs/MarketingConsent'
import {PhoneNumberField} from '@/components/inputs/PhoneNumberField'
import {TextInput} from '@/components/inputs/TextInput'
import {Spinner} from '@/components/Spinner'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {Trans, useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {TrademarkVerificationFields} from '../types'

export const ContactDetails: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation()
  const {
    contactDetails: {
      title,
      firstNameLabel,
      firstNamePlaceholder,
      lastNameLabel,
      lastNamePlaceholder,
      emailLabel,
      emailPlaceholder,
      phoneLabel,
      phoneNote,
      skipVerification,
      loggedInClientDescription,
    },
  } = useTrademarkVerificationContent()
  const {values, errors, handleBlur, setFieldValue, isSubmitting} =
    useFormikContext<TrademarkVerificationFields>()

  const validEmail = !errors.contact_email && values.contact_email ? values.contact_email : null
  const {
    user,
    isLoading,
    isAdmin,
    isLoggedInCustomer,
    visibleAuthForm,
    hasAccount,
    signInNotice,
    checkUserExistence,
  } = useContactAccountState(validEmail, values.contact_email, (email) =>
    setFieldValue('contact_email', email),
  )
  const showMarketingConsent =
    !isLoggedInCustomer || user?.profile?.marketing_emails_enabled === false

  return (
    <>
      {isLoading ? (
        <div className="spinner-wrapper">
          <Spinner color={theme.palette.text.default} />
        </div>
      ) : (
        <FormSection
          title={t(title)}
          description={
            isLoggedInCustomer ? (
              <Text variant={TextVariant.text14}>
                <Trans
                  i18nKey={loggedInClientDescription.text}
                  components={{
                    1: <TransLink href={loggedInClientDescription.linkHref} />,
                  }}
                />
              </Text>
            ) : undefined
          }
        >
          {isLoggedInCustomer && user ? (
            <AccountInfoCard user={user} />
          ) : (
            <>
              <FormItemsWrapper
                leftItem={
                  <FormItem label={t(firstNameLabel)}>
                    <ErrorMessage
                      name="contact_first_name"
                      render={(msg) => <FormErrorMessage message={msg} />}
                    />
                    <TextInput
                      name="contact_first_name"
                      data-test="contactFirstName"
                      placeholder={t(firstNamePlaceholder)}
                    />
                  </FormItem>
                }
                rightItem={
                  <FormItem label={t(lastNameLabel)}>
                    <ErrorMessage
                      name="contact_last_name"
                      render={(msg) => <FormErrorMessage message={msg} />}
                    />
                    <TextInput
                      name="contact_last_name"
                      data-test="contactLastName"
                      placeholder={t(lastNamePlaceholder)}
                    />
                  </FormItem>
                }
              />
              <div className="item">
                <FormItem label={t(emailLabel)}>
                  <ErrorMessage
                    name="contact_email"
                    render={(msg) => <FormErrorMessage message={msg} />}
                  />
                  <TextInput
                    onBlur={(e) => {
                      handleBlur(e)
                      checkUserExistence(validEmail)
                    }}
                    type="email"
                    name="contact_email"
                    data-test="contactEmail"
                    id="gtm-email"
                    placeholder={t(emailPlaceholder)}
                  />
                </FormItem>
              </div>
              <div className="item">
                <FormItem label={t(phoneLabel)}>
                  <ErrorMessage
                    name="contact_phone"
                    render={(msg) => <FormErrorMessage message={msg} />}
                  />
                  <PhoneNumberField name="contact_phone" dataTest="contactPhoneNumber" />
                </FormItem>
                <div className="phone-note">
                  <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                    {t(phoneNote)}
                  </Text>
                </div>
              </div>
            </>
          )}
          <div className={cn('notice', visibleAuthForm && hasAccount && 'visible')}>
            {signInNotice}
          </div>
          {showMarketingConsent && (
            <div className="checkbox">
              <MarketingConsent isDisabled={isSubmitting} />
            </div>
          )}
          <div className={cn('signup', visibleAuthForm && !hasAccount && 'visible')}>
            <FormSignUp isDisabled={isSubmitting} />
          </div>
          {isAdmin && (
            <div className="checkbox">
              <Checkbox
                name="skip_verification"
                label={<Text>{skipVerification}</Text>}
                isBoolean
                isDisabled={isSubmitting}
              />
            </div>
          )}
        </FormSection>
      )}

      <style jsx>{`
        .item {
          margin-top: ${theme.spacing[3]};
        }

        .checkbox {
          margin-top: ${theme.spacing[5]};
        }

        .checkbox + .checkbox {
          margin-top: ${theme.spacing[4]};
        }

        .phone-note {
          margin-top: ${theme.spacing[1]};
        }

        .notice,
        .signup {
          opacity: 0;
          visibility: hidden;
          max-height: 0;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }

        .visible {
          opacity: 1;
          visibility: visible;
          max-height: 200px;
        }

        .notice.visible {
          margin-top: ${theme.spacing[3]};
          margin-bottom: ${theme.spacing[4]};
        }

        .signup.visible {
          margin-top: ${theme.spacing[4]};
        }

        .spinner-wrapper {
          display: flex;
          align-items: center;
          height: 100%;
        }

        @media ${TABLET_SCREENSIZE} {
          .spinner-wrapper {
            margin: ${theme.spacing[9]} 0;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .spinner-wrapper {
            margin: ${theme.spacing[8]} 0;
          }
        }
      `}</style>
    </>
  )
}
